import { Link } from 'react-router';
import { useAppState, useLearner } from '@/app/useAppState';
import { Toggle } from '@/components/ui/Field';
import { Card } from '@/components/ui/Card';
import { ScreenHeader } from '@/components/ui/ScreenHeader';
import { SampleBadge } from '@/components/ui/Badges';
import { curriculum } from '@/content/curriculum';
import { LanguagePicker } from '@/features/learners/LanguagePicker';
import { useI18n } from '@/i18n/context';
import { isolateAuto } from '@/lib/bidi';

export function SettingsScreen() {
  const learner = useLearner();
  const { updatePreferences } = useAppState();
  const { t, l } = useI18n();
  const name = learner.nickname ? isolateAuto(learner.nickname) : t('profiles.anonymous');
  const prefs = learner.preferences;
  const version = curriculum.pack.curriculumVersion;

  return (
    <div className="flex flex-col gap-6">
      <ScreenHeader title={t('settings.title')} />
      <Card className="flex flex-col gap-3">
        <h2 className="text-lg font-extrabold">{t('settings.language')}</h2>
        <LanguagePicker className="self-start" />
      </Card>
      <Card className="flex flex-col">
        <h2 className="mb-2 text-lg font-extrabold">{t('settings.preferences', { name })}</h2>
        <div className="divide-y divide-line">
          <Toggle
            label={t('onboarding.soundEffects')}
            checked={prefs.soundEffects}
            onChange={(v) => void updatePreferences({ soundEffects: v })}
          />
          <Toggle
            label={t('onboarding.speech')}
            checked={prefs.speech}
            onChange={(v) => void updatePreferences({ speech: v })}
          />
          <Toggle
            label={t('onboarding.largeText')}
            checked={prefs.largeText}
            onChange={(v) => void updatePreferences({ largeText: v })}
          />
          <Toggle
            label={t('onboarding.reducedMotion')}
            checked={prefs.reducedMotion}
            onChange={(v) => void updatePreferences({ reducedMotion: v })}
          />
        </div>
      </Card>
      <Card className="flex flex-col gap-2">
        <h2 className="text-lg font-extrabold">{t('settings.contentStatus')}</h2>
        <SampleBadge className="self-start" />
        <p className="text-sm text-ink-muted">{l(version.label)}</p>
        <p className="text-sm text-ink-muted">
          {t('settings.contentVersion', { version: version.id })}
        </p>
      </Card>
      <nav className="flex flex-col gap-2 font-bold">
        <Link to="/whats-new" className="rounded-2xl bg-surface p-4 shadow-[var(--shadow-card)]">
          <span aria-hidden>🎉 </span>
          {t('whatsNew.title')}
        </Link>
        <Link to="/privacy" className="rounded-2xl bg-surface p-4 shadow-[var(--shadow-card)]">
          {t('settings.privacyLink')}
        </Link>
        <Link to="/parent" className="rounded-2xl bg-surface p-4 shadow-[var(--shadow-card)]">
          {t('welcome.parentArea')}
        </Link>
        <Link to="/profiles" className="rounded-2xl bg-surface p-4 shadow-[var(--shadow-card)]">
          {t('profiles.switch')}
        </Link>
      </nav>
    </div>
  );
}
